import { Configuration, FrontendApi } from '@ory/kratos-client';
import type { ReactElement } from 'react';
import type { NonIndexRouteObject } from 'react-router';
import type { Merge } from 'type-fest';

import { LOGIN, type PageTitles } from '@revelio/core';
import { Role, Tab } from '@revelio/data-access';

import { isRoleRevelioAdmin } from './auth.ory.hooks';

export interface NavItemConfig extends Omit<NonIndexRouteObject, 'children'> {
  name: string | PageTitles;
  serverTab: Tab | Tab[];
  icon?: string | ReactElement;
  admin?: boolean;
  clientAdmin?: boolean;
  parent?: string | PageTitles;
  subHeading?: string;
  divider?: boolean;
  elementId?: string;
  isSourcesTab?: boolean;
  excludeFromSideNavButtons?: boolean;
}

export interface RouteNavConfig extends Partial<NavItemConfig> {
  children?: RouteNavConfig[];
  nestedRoutes?: RouteNavConfig[];
}

export enum RouterRoutePaths {
  LANDING = '/',
  OVERVIEW = '/company/compositions',
  TRANSITION = '/company/transitions',
  POSTING = '/company/postings',
  SENTIMENT = '/company/sentiment-ratings',
  SCREENER = '/company/screener',
  TALENT_DISCOVERY = '/talent-discovery',
  RESUME_ENRICHMENT = '/resume-enrichment',
  ENRICHED_RESUME = '/resume-enrichment/:id',
  DATA_DICTIONARY = '/data-dictionary',
  MANAGE = '/manage',
}

export const ServerProfileTabsPathMap: Merge<
  { [key in Tab]?: RouterRoutePaths },
  { landing: RouterRoutePaths }
> = {
  landing: RouterRoutePaths.LANDING,
  [Tab.Overview]: RouterRoutePaths.OVERVIEW,
  [Tab.Transition]: RouterRoutePaths.TRANSITION,
  [Tab.Posting]: RouterRoutePaths.POSTING,
  [Tab.Sentiment]: RouterRoutePaths.SENTIMENT,
  [Tab.Screener]: RouterRoutePaths.SCREENER,
  [Tab.TalentDiscovery]: RouterRoutePaths.TALENT_DISCOVERY,
  [Tab.DataDictionary]: RouterRoutePaths.DATA_DICTIONARY,
};

interface AuthgInitConfig {
  oryKratosRoot?: string;
  dashMetaRoot?: string;
  accountPortalRoot?: string;
  accountPortalBackendRoot?: string;
}

export let DASH_META_ROOT = '';
export let ACCOUNT_PORTAL_ROOT = '';
export let ACCOUNT_PORTAL_BACKEND_ROOT = '';
export let ORY_KRATOS_ROOT = '';
export let oryClient: FrontendApi | null = null;
export const trialLength = 7;

export function initAuth(config: AuthgInitConfig) {
  DASH_META_ROOT = config.dashMetaRoot || '';
  ACCOUNT_PORTAL_ROOT = config.accountPortalRoot || '';
  ACCOUNT_PORTAL_BACKEND_ROOT = config.accountPortalBackendRoot || '';
  ORY_KRATOS_ROOT = config.oryKratosRoot || '';
  oryClient = new FrontendApi(
    new Configuration({
      basePath: ORY_KRATOS_ROOT,
      baseOptions: {
        withCredentials: true,
      },
    })
  );
  return Promise.resolve(true);
}

export const MIN_REQUIRED_TABS_FOR_ENTITY_SUMMARY = [
  Tab.TalentDiscovery,
  Tab.Overview,
  Tab.Posting,
  Tab.Sentiment,
  Tab.Compensation,
];

export const hasEntitySummaryTab = (userTabs: Tab[]) =>
  MIN_REQUIRED_TABS_FOR_ENTITY_SUMMARY.every((t) => {
    if (Array.isArray(t)) {
      return t.some((t) => userTabs.includes(t));
    }
    return userTabs.includes(t);
  });

export const isTabAllowed = (
  tab: Tab | Tab[] | typeof LOGIN,
  userTabs: Tab[]
) => {
  if (tab === Tab.EntitySummary) {
    return hasEntitySummaryTab(userTabs);
  }

  if (Array.isArray(tab)) {
    return tab.some((t) => userTabs.includes(t));
  }

  return tab === LOGIN || userTabs.includes(tab);
};

export function filterNavItemsForUser(
  items: RouteNavConfig[],
  userServerTabs: Tab[] = [],
  userRole?: Role | null
) {
  // TODO: Do we really need this admin check? NavItems have
  // a clientAdmin check so this might not be needed.
  const isAdmin = isRoleRevelioAdmin(userRole);
  const isClientAdmin = userRole === Role.ClientAdmin;
  return items.reduce((result, r) => {
    if (!r.serverTab && !r.children) {
      const adminCheckOk =
        (r.admin && isAdmin) ||
        (r.clientAdmin && (isAdmin || isClientAdmin)) ||
        (!r.admin && !r.clientAdmin);
      const shouldAddItem = r.icon ? adminCheckOk : false;
      if (shouldAddItem) {
        result.push(r);
      }
      return result;
    }

    if (r.children) {
      const filteredChildren = filterNavItemsForUser(
        r.children,
        userServerTabs,
        userRole
      );
      if (filteredChildren.length) {
        result.push({ ...r, children: filteredChildren });
      }
      return result;
    }

    if (r.serverTab && isTabAllowed(r.serverTab, userServerTabs)) {
      if (r.admin) {
        isAdmin && result.push(r);
        return result;
      }
      result.push(r);
      return result;
    }

    return result;
  }, [] as RouteNavConfig[]);
}
